import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../../../store/store';
import { EstimatedTendsChart } from '../../../Analytics/statsComponents/charts/estimatedTends/EstimatedTends';
import { SellingZone } from '../../../Analytics/statsComponents/charts/sellingZone/SellingZone';
import { UpperHandChart } from '../../../Analytics/statsComponents/charts/upperHand/UpperHand';
import { formatNavigationCellValue } from '../../utils/teamq.utils';
import { QCellSkeleton } from '../skeletons/QCellSkeleton';

export const TeamOverview: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const {
    requestedTeamId,
    requestedTimePeriodDays,
    activity,
    activityStatus,
    upperHand,
    upperHandStatus,
    estTendencies,
    estTendenciesStatus,
    sellingZone,
    sellingZoneStatus,
  } = useSelector((state: RootState) => state.teamQ);

  const history = useHistory();

  const goToActivity = () => {
    if (user) {
      history.push(
        `/orgs/${user.orgId}/users/${user.userId}/team-analytics/activity`
      );
    }
  };

  const goToAnalytics = () => {
    if (user) {
      history.push(
        `/orgs/${user.orgId}/users/${user.userId}/team-analytics/analytics`
      );
    }
  };

  const attrs = {
    wrapper: {
      className: 'team-q-overview',
    },

    titleRow: {
      wrapper: {
        className: 'team-q-overview__title-row',
      },

      label: {
        className: 'team-q-overview__title-row-label',
      },

      activityLink: {
        className: 'team-q-overview__title-row-link',
        onClick: goToActivity,
      },

      analyticsLink: {
        className: 'team-q-overview__title-row-link',
        onClick: goToAnalytics,
      },
    },

    activitySection: {
      wrapper: {
        className: 'team-q-overview__activity-section',
      },

      divider: {
        className: 'team-q-overview__activity-section-divider',
      },

      cell: {
        wrapper: {
          className: 'team-q-overview__activity-section__cell',
        },

        title: {
          className: 'team-q-overview__activity-section__cell-title',
        },

        value: {
          normal: {
            className: 'team-q-overview__activity-section__cell__value',
          },

          bold: {
            className: 'team-q-overview__activity-section__cell__value-bold',
          },

          noData: {
            className: 'team-q-overview__activity-section__cell__value-n-a',
          },
        },
      },
    },

    chartSection: {
      wrapper: {
        className: 'team-q-overview__chart-section',
      },

      divider: {
        className: 'team-q-overview__chart-section-divider',
      },

      UhPossessions: {
        upperHandData: upperHand,
        isLoading: upperHandStatus?.isLoading,
        noData: upperHandStatus?.noData,
        error: upperHandStatus?.error,
        isInTeamQ: true,
        lastDays: requestedTimePeriodDays ? requestedTimePeriodDays + 1 : 0,
      },

      EstimatedTends: {
        data: estTendencies,
        isLoading: estTendenciesStatus.isLoading,
        noData: estTendenciesStatus?.noData,
        error: estTendenciesStatus.error,
        isInTeamQ: true,
        lastDays: requestedTimePeriodDays ? requestedTimePeriodDays + 1 : 0,
      },

      SellingZone: {
        sellZoneData: sellingZone?.data,
        isLoading: sellingZoneStatus.isLoading,
        noData: sellingZoneStatus?.noData,
        error: sellingZoneStatus.error,
        isInTeamQ: true,
        lastDays: requestedTimePeriodDays ? requestedTimePeriodDays + 1 : 0,
        requestedTeamQId: requestedTeamId || '',
      },
    },
  };

  const activityHeadline = (
    <div {...attrs.titleRow.wrapper}>
      <div {...attrs.titleRow.label}>Team Activity</div>
      <div {...attrs.titleRow.activityLink}>Go to Detailed View</div>
    </div>
  );

  const analyticsHeadline = (
    <div {...attrs.titleRow.wrapper}>
      <div {...attrs.titleRow.label}>Team Analytics</div>
      <div {...attrs.titleRow.analyticsLink}>Go to Detailed View</div>
    </div>
  );

  const createCell = (
    title: string,
    value: number | 'N/A',
    avgText: string
  ) => {
    const definedValue =
      value === 'N/A' ? 'N/A' : formatNavigationCellValue(value);

    const valueToShow =
      definedValue === 'N/A' ? (
        <div {...attrs.activitySection.cell.value.noData}>N/A</div>
      ) : (
        <>
          <div {...attrs.activitySection.cell.value.bold}>{definedValue}</div>
          {avgText}
        </>
      );

    return (
      <div {...attrs.activitySection.cell.wrapper}>
        <div {...attrs.activitySection.cell.title}>{title}</div>
        <QCellSkeleton isLoading={activityStatus.isLoading}>
          <div {...attrs.activitySection.cell.value.normal}>{valueToShow}</div>
        </QCellSkeleton>
      </div>
    );
  };

  const activitySection = (
    <div {...attrs.activitySection.wrapper}>
      {createCell(
        'Thread Duration',
        activity?.avgThreadDurationWeeks || 'N/A',
        `${
          activity?.avgThreadDurationWeeks === 1 ? 'Week' : 'Weeks'
        }, Team Avg.`
      )}

      <div {...attrs.activitySection.divider} />

      {createCell(
        'Thread Length',
        activity?.avgMessagesPerThread || 'N/A',
        `${
          activity?.avgMessagesPerThread === 1 ? 'Message' : 'Messages'
        }, Team Avg.`
      )}

      <div {...attrs.activitySection.divider} />

      {createCell(
        'Messages Analyzed',
        activity?.emailsAnalyzedAvg || 'N/A',
        'Avg/Team Member '
      )}

      <div {...attrs.activitySection.divider} />

      {createCell(
        'Messages Simulated',
        activity?.emailsSimulatedAvg || 'N/A',
        'Avg/Team Member '
      )}
    </div>
  );

  const chartSection = (
    <div {...attrs.chartSection.wrapper}>
      <UpperHandChart {...attrs.chartSection.UhPossessions} />
      <div {...attrs.chartSection.divider} />
      <EstimatedTendsChart {...attrs.chartSection.EstimatedTends} />
      <div {...attrs.chartSection.divider} />
      <SellingZone {...attrs.chartSection.SellingZone} />
    </div>
  );

  return (
    <div {...attrs.wrapper}>
      {activityHeadline}
      {activitySection}
      {analyticsHeadline}
      {chartSection}
    </div>
  );
};
